import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import cart from "../assets/images/icons/orange-cart.png";
import clock from "../assets/images/icons/orange-clock.png";
import tick from "../assets/images/icons/orange-tick.png";
import cancel from "../assets/images/icons/orange-cancel.png";
import cancel2 from "../assets/images/navbar/cancel.png";
import offline from "../assets/images/offline.png";
import table from "../assets/images/table.png";
import { useRestaurant } from "../context/RestaurantContext";
import { restaurantService } from "../services";
import { useNotifications } from "../context/NotificationContext";

const TopCard: React.FC = () => {
  const navigate = useNavigate();
  const {
    restaurantProfile,
    isLoadingProfile,
    isRestaurantOpen,
    setIsRestaurantOpen,
    setDashboardData,
    orderStats,
  } = useRestaurant();
  const { unreadCount } = useNotifications();
  const [activeTab, setActiveTab] = useState<
    "Today" | "This Week" | "This Month" | "Custom Range"
  >("Today");

  const [showModal, setShowModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [autoTurnOn, setAutoTurnOn] = useState(true); // Toggle state for modal
  const [scheduledDateTime, setScheduledDateTime] = useState<string>("");

  // TEMPORARY: Set to true to test UI without backend (remove this when backend is ready)
  const MOCK_MODE = false;

  // Fetch dashboard data based on active tab
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!isRestaurantOpen) {
        return; // Don't fetch if restaurant is closed
      }

      try {
        // Map tab to API period
        const periodMap: Record<string, "today" | "week" | "month"> = {
          Today: "today",
          "This Week": "week",
          "This Month": "month",
        };

        const period = periodMap[activeTab] || "today";

        console.log(`Fetching dashboard data for period: ${period}`);

        const response = await restaurantService.getDashboardData(period);

        if (response.status && response.data) {
          console.log("Dashboard data fetched successfully:", response.data);
          setDashboardData(response.data);
        }
      } catch (error: any) {
        console.error("Error fetching dashboard data:", error);
        // Don't show alert for dashboard fetch errors, just log them
      }
    };

    fetchDashboardData();
  }, [activeTab, isRestaurantOpen, setDashboardData, MOCK_MODE]);

  const carts = [
    {
      count: orderStats?.newOrders || 0,
      image: cart,
      title: "Total Orders",
    },
    {
      count: orderStats?.pendingOrders || 0,
      image: clock,
      title: "Pending Orders",
    },
    {
      count: orderStats?.completedOrders || 0,
      image: tick,
      title: "Delivered Orders",
    },
    {
      count: orderStats?.cancelledOrders || 0,
      image: cancel,
      title: "Cancelled Orders",
    },
  ];

  const handleToggle = async () => {
    if (isRestaurantOpen) {
      // Closing restaurant
      try {
        console.log("Calling API to close restaurant...");

        if (MOCK_MODE) {
          // Mock mode - just update local state for testing
          console.log("MOCK MODE: Restaurant closed (no API call)");
          setIsRestaurantOpen(false);
          return;
        }

        const response = await restaurantService.toggleRestaurantStatus(false);
        console.log("Restaurant closed successfully:", response);
        setIsRestaurantOpen(false);
      } catch (error: any) {
        console.error("Error closing restaurant:", error);
        const errorMessage =
          error?.message || "Failed to close restaurant. Please try again.";
        alert(
          errorMessage +
            "\n\nPlease verify the API endpoint with your backend team.",
        );
      }
    } else {
      // Opening restaurant - show modal
      setAutoTurnOn(true); // Default to ON
      setSelectedOption("");
      setScheduledDateTime("");
      setShowModal(true);
    }
  };

  const handleTurnOnRestaurant = async () => {
    if (!autoTurnOn && !selectedOption) {
      alert("Please select an option");
      return;
    }

    if (
      !autoTurnOn &&
      selectedOption === "specific_datetime" &&
      !scheduledDateTime
    ) {
      alert("Please select a date and time");
      return;
    }

    try {
      if (autoTurnOn) {
        // Turn on immediately
        console.log("Calling API to open restaurant immediately...");

        if (MOCK_MODE) {
          console.log("MOCK MODE: Restaurant opened immediately (no API call)");
          setIsRestaurantOpen(true);
          setShowModal(false);
          setSelectedOption("");
          setScheduledDateTime("");
          return;
        }

        const response = await restaurantService.toggleRestaurantStatus(true);
        console.log("Restaurant opened successfully:", response);
        setIsRestaurantOpen(true);
      } else {
        // Schedule auto-open
        console.log("Calling API to schedule restaurant auto-open...");

        const scheduleData: { type: string; scheduledAt?: string } = {
          type: selectedOption,
        };

        if (selectedOption === "specific_datetime") {
          scheduleData.scheduledAt = scheduledDateTime;
        }

        if (MOCK_MODE) {
          console.log(
            "MOCK MODE: Restaurant scheduled (no API call)",
            scheduleData,
          );
          alert(
            `Restaurant scheduled to open at ${selectedOption === "specific_datetime" ? scheduledDateTime : selectedOption}`,
          );
          setShowModal(false);
          setSelectedOption("");
          setScheduledDateTime("");
          return;
        }

        const response = await restaurantService.scheduleAutoOpen(scheduleData);
        console.log("Restaurant scheduled successfully:", response);
        alert(`Restaurant scheduled to open successfully!`);
      }

      setShowModal(false);
      setSelectedOption("");
      setScheduledDateTime("");
    } catch (error: any) {
      console.error("Error with restaurant operation:", error);
      const errorMessage =
        error?.message ||
        "Failed to update restaurant status. Please try again.";
      alert(
        errorMessage +
          "\n\nPlease verify the API endpoint with your backend team.",
      );
    }
  };

  return (
    <div>
      {/* Mobile Header - Restaurant Name & Toggle (only show when open) */}
      {isRestaurantOpen && (
        <div className="md:hidden flex justify-between items-center px-4 py-3 bg-white dark:bg-gray-900 border-b dark:border-gray-700 transition-colors">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-200">
                {restaurantProfile?.businessName}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {restaurantProfile?.location?.address}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Notification Icon */}
            <button
              onClick={() => navigate("/notification")}
              className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors dark:border-2 dark:border-white"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="dark:invert"
              >
                <path
                  d="M12.02 2.91003C8.71003 2.91003 6.02003 5.60003 6.02003 8.91003V11.8C6.02003 12.41 5.76003 13.34 5.45003 13.86L4.30003 15.77C3.59003 16.95 4.08003 18.26 5.38003 18.7C9.69003 20.14 14.34 20.14 18.65 18.7C19.86 18.3 20.39 16.87 19.73 15.77L18.58 13.86C18.28 13.34 18.02 12.41 18.02 11.8V8.91003C18.02 5.61003 15.32 2.91003 12.02 2.91003Z"
                  stroke="#1F2937"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                />
                <path
                  d="M13.87 3.19994C13.56 3.10994 13.24 3.03994 12.91 2.99994C11.95 2.87994 11.03 2.94994 10.17 3.19994C10.46 2.45994 11.18 1.93994 12.02 1.93994C12.86 1.93994 13.58 2.45994 13.87 3.19994Z"
                  stroke="#1F2937"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.02 19.0601C15.02 20.7101 13.67 22.0601 12.02 22.0601C11.2 22.0601 10.44 21.7201 9.90002 21.1801C9.36002 20.6401 9.02002 19.8801 9.02002 19.0601"
                  stroke="#1F2937"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#FF8A00] text-white text-[9px] font-semibold flex items-center justify-center">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="w-[95%] md:w-full mx-auto mt-[104px]">
        {/* Toggle section - show simplified version when closed, full version when open */}
        {isRestaurantOpen ? (
          <div className="flex md:hidden justify-between items-center pl-4 py-3 ">
            {/* Toggle */}
            <label className="inline-flex items-center cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isRestaurantOpen}
                  onChange={handleToggle}
                />
                <div className="relative w-[70px] h-[28px] bg-gray-300 rounded-full peer peer-checked:after:translate-x-[42px] after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-[24px] after:w-[24px] after:transition-all peer-checked:bg-[#ff8a00]">
                  <span
                    className={`absolute top-1/2 -translate-y-1/2 text-[10px] font-medium transition-all duration-200 ${
                      isRestaurantOpen
                        ? "left-1.5 text-white"
                        : "right-1.5 text-gray-700"
                    }`}
                  >
                    {isRestaurantOpen ? "ON" : "OFF"}
                  </span>
                </div>
              </div>
            </label>

            <div className="flex justify-between items-center w-[142px] h-[36px] top-[123px] left-[310px] rounded-tl-[8px] rounded-bl-[8px] p-[4px] gap-[4px] opacity-100 bg-[#7402A7] text-white font-Urbanist text-[14px] font-medium leading-[22px]">
              <img src={table} alt="Table" className="h-[30px] w-[30px]" />
              <span>Table Bookings</span>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center py-6 bg-white dark:bg-gray-900 transition-colors">
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">Turn on your restaurant</p>
              <label className="inline-flex items-center cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isRestaurantOpen}
                    onChange={handleToggle}
                  />
                  <div className="relative w-[70px] h-[28px] bg-gray-300 rounded-full peer peer-checked:after:translate-x-[42px] after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-[24px] after:w-[24px] after:transition-all peer-checked:bg-[#ff8a00]">
                    <span
                      className={`absolute top-1/2 -translate-y-1/2 text-[10px] font-medium transition-all duration-200 ${
                        isRestaurantOpen
                          ? "left-1.5 text-white"
                          : "right-1.5 text-gray-700"
                      }`}
                    >
                      {isRestaurantOpen ? "ON" : "OFF"}
                    </span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Tabs - Hide when restaurant is closed */}
        <div
          className={`${!isRestaurantOpen ? "hidden" : "flex"} justify-between items-center px-4 md:px-0 pt-4 bg-[#F5E6D3] dark:bg-gray-800 rounded-t-xl transition-colors`}
        >
          {/* Glass Morphism Pill Tabs */}
          <div className="flex w-full md:w-[85%] md:mx-auto">
            <div className="inline-flex gap-2 p-1 md:p-0 rounded-xl md:rounded-none bg-gradient-to-r from-[#F5E6D3] to-[#F5D4B8] dark:from-gray-700 dark:to-gray-600 md:bg-none md:dark:bg-none backdrop-blur-sm md:backdrop-blur-none shadow-inner md:shadow-none w-full md:w-auto">
              <button
                onClick={() => setActiveTab("Today")}
                className={`relative px-4 py-2 md:py-1 text-xs md:text-sm font-medium  whitespace-nowrap rounded-xl flex-1 md:flex-none
              ${
                activeTab === "Today"
                  ? "bg-gradient-to-br from-[#FFE8D1] to-[#FFD4B3] dark:from-orange-600 dark:to-orange-500 shadow-md text-gray-900 md:text-[#FF8A00] dark:text-white border-2 border-white dark:border-gray-600"
                  : "bg-[linear-gradient(180deg,rgba(255,255,255,0.5)_0%,rgba(242,211,183,0.85)_100%)] dark:bg-gray-700 text-gray-600 md:text-gray-500 dark:text-gray-300 hover:text-gray-900 md:hover:text-gray-700 dark:hover:text-gray-100"
              }`}
              >
                Today
                {/* {activeTab === "Today" && (
                <span className="hidden md:block absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )} */}  
              </button>

              <button
                onClick={() => setActiveTab("This Week")}
                className={`relative px-4 py-2 md:py-1 text-xs md:text-sm font-medium  whitespace-nowrap rounded-xl flex-1 md:flex-none
              ${
                activeTab === "This Week"
                  ? "bg-gradient-to-br from-[#FFE8D1] to-[#FFD4B3] dark:from-orange-600 dark:to-orange-500 shadow-md text-gray-900 md:text-[#FF8A00] dark:text-white border-2 border-white dark:border-gray-600"
                  : "bg-[linear-gradient(180deg,rgba(255,255,255,0.5)_0%,rgba(242,211,183,0.85)_100%)] dark:bg-gray-700 text-gray-600 md:text-gray-500 dark:text-gray-300 hover:text-gray-900 md:hover:text-gray-700 dark:hover:text-gray-100"
              }`}
              >
                This Week
                {/* {activeTab === "This Week" && (
                <span className="hidden md:block absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )} */}
              </button>

              <button
                onClick={() => setActiveTab("This Month")}
                className={`relative px-4 py-2 md:py-1 text-xs md:text-sm font-medium  whitespace-nowrap rounded-xl flex-1 md:flex-none
              ${
                activeTab === "This Month"
                  ? "bg-gradient-to-br from-[#FFE8D1] to-[#FFD4B3] dark:from-orange-600 dark:to-orange-500 shadow-md text-gray-900 md:text-[#FF8A00] dark:text-white border-2 border-white dark:border-gray-600"
                  : "bg-[linear-gradient(180deg,rgba(255,255,255,0.5)_0%,rgba(242,211,183,0.85)_100%)] dark:bg-gray-700 text-gray-600 md:text-gray-500 dark:text-gray-300 hover:text-gray-900 md:hover:text-gray-700 dark:hover:text-gray-100"
              }`}
              >
                This Month
                {/* {activeTab === "This Month" && (
                <span className="hidden md:block absolute left-0 bottom-0 h-[2px] w-full bg-[#FF8A00] rounded-full"></span>
              )} */}
              </button>
            </div>
          </div>

          <div className="hidden md:block">
            <label className="inline-flex items-center me-5 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isRestaurantOpen}
                  onChange={handleToggle}
                />
                <div className="relative w-[90px] h-[23px] bg-gray-300 rounded-full peer peer-checked:after:translate-x-[60px] rtl:peer-checked:after:-translate-x-[60px] after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-[19px] after:w-[27px] after:transition-all peer-checked:bg-[#ff8a00]">
                  <span
                    className={`absolute top-1/2 -translate-y-1/2 text-xs font-medium transition-all duration-200 ${isRestaurantOpen ? "left-2 text-white" : "right-2 text-gray-700"}`}
                  >
                    {isRestaurantOpen ? "Open" : "Closed"}
                  </span>
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Offline Section - Show when restaurant is closed */}
        {!isRestaurantOpen && (
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] md:min-h-[400px] bg-white dark:bg-gray-900 px-4 py-12 transition-colors">
            <div className="text-center max-w-md">
              <div className="mb-8 flex justify-center">
                <img src={offline} alt="Offline" className="w-full max-w-xs" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">
                You are Offline
              </h2>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
                Turn on your restaurant to see analytics and orders
              </p>
            </div>
          </div>
        )}

        {/* Cards Section - Show when restaurant is open */}
        {isRestaurantOpen && (
          <div className="w-full px-4 md:px-[127px] py-6 md:py-[50px] bg-gradient-to-b from-[#F5E6D3] via-[#F5D4B8] to-[#F5C9AA] dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 rounded-b-xl transition-colors">
            <div className="grid grid-cols-2 md:flex md:flex-row items-start gap-3 md:gap-8">
              {carts.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col md:flex-row justify-between items-start rounded-[16px] md:rounded-[20px] md:w-[265px] px-4 md:px-[20px] py-4 md:py-[18px] border-[1.5px] md:border-[1.654px] border-white/30 dark:border-gray-600/30 bg-gradient-to-br from-[#FFE8D1] via-[#FFDCBF] to-[#FFD4B3] dark:from-gray-700 dark:via-gray-700 dark:to-gray-600 backdrop-blur-md shadow-[0_8px_24px_rgba(255,138,0,0.12)] md:shadow-[0_13.231px_32px_rgba(255,138,0,0.15)] hover:shadow-[0_12px_32px_rgba(255,138,0,0.2)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.3)] transition-colors"
                >
                  <div className="flex flex-col justify-between items-start w-full">
                    <div className="w-full flex justify-between">
                      <h5 className="text-[#FF8A00] font-Urbanist text-[32px] md:text-[39.692px] font-bold leading-[120%] mb-1">
                        {item.count.toString().padStart(2, "0")}
                      </h5>
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-[20px] w-[20px] md:h-[24px] md:w-[24px]"
                      />
                    </div>
                    <div className="mt-1 md:mt-0 p-2 rounded-lg">
                      <h5 className="text-[#050505] dark:text-gray-200 font-Urbanist text-[13px] md:text-[16px] font-medium leading-[130%]">
                        {item.title}
                      </h5>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Turn On Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
            <div className="bg-white dark:bg-gray-800 rounded-t-[24px] md:rounded-2xl p-6 pb-8 md:pb-6 w-full md:w-[400px] relative animate-slide-up md:animate-none max-h-[90vh] md:max-h-none overflow-y-auto transition-colors">
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedOption("");
                  setScheduledDateTime("");
                }}
                className="absolute top-4 right-4 border dark:border-gray-600 rounded-full p-1 z-10 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <img
                  src={cancel2}
                  alt="Cancel"
                  className="h-[22px] w-[22px] dark:invert"
                />
              </button>

              <div className="flex justify-between items-center mt-8">
                <h3 className="text-[17px] font-medium text-[#050505] dark:text-gray-200 mb-6 leading-[25px]">
                  Automatically Turn On
                </h3>

                <div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={autoTurnOn}
                      onChange={() => setAutoTurnOn(!autoTurnOn)}
                    />
                    <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
              </div>

              <div
                className={`space-y-[20px] mb-6 transition-opacity ${autoTurnOn ? "opacity-50 pointer-events-none" : ""}`}
              >
                {[
                  { value: "2_hours", label: "2 hours" },
                  { value: "4_hours", label: "4 hours" },
                  {
                    value: "tomorrow_first_slot",
                    label: "Tomorrow before first slot",
                  },
                  {
                    value: "specific_datetime",
                    label: "Specific date and time",
                  },
                ].map((option) => (
                  <div key={option.value}>
                    <label className="flex items-center justify-between px-3 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                      <span className="text-[#050505] dark:text-gray-200 font-Urbanist font-medium leading-[25px]">
                        {option.label}
                      </span>
                      <input
                        type="radio"
                        name="turnOnOption"
                        value={option.value}
                        checked={selectedOption === option.value}
                        onChange={(e) => {
                          setSelectedOption(e.target.value);
                          if (e.target.value !== "specific_datetime") {
                            setScheduledDateTime("");
                          }
                        }}
                        className="w-5 h-5 text-orange-500 focus:ring-orange-500"
                        disabled={!autoTurnOn}
                      />
                    </label>
                    {option.value === "specific_datetime" &&
                      selectedOption === "specific_datetime" &&
                      autoTurnOn && (
                        <div className="mt-3 px-3">
                          <input
                            type="datetime-local"
                            value={scheduledDateTime}
                            onChange={(e) =>
                              setScheduledDateTime(e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                            min={new Date().toISOString().slice(0, 16)}
                          />
                        </div>
                      )}
                  </div>
                ))}
              </div>

              <button
                onClick={handleTurnOnRestaurant}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-400 text-white font-semibold py-3 rounded-lg hover:from-orange-600 hover:to-orange-500 transition-all"
              >
                {autoTurnOn ? "Turn On Restaurant Now!" : "Schedule Turn On"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopCard;
